import {gql} from '@apollo/client';
import {objectFromEntries} from '@benchling/js-utils';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {
  ContainersSearchListingExperimentalRoleQuery,
  ContainersSearchListingExperimentalRoleQueryVariables,
} from 'samples/containers/__generated/ContainersSearchListingExperimentalRoleQuery';
import {ExperimentalRole} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {ListableItem} from 'search/SearchConstants';
import APIUtil from 'util/APIUtil';

export const CONTAINERS_SEARCH_LISTING_EXPERIMENTAL_ROLE_QUERY = gql`
  query ContainersSearchListingExperimentalRoleQuery($containerIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        role {
          primaryRole
          subrole
          group
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface ExperimentalRolesByContainerId {
  [containerId: string]: ExperimentalRole | null;
}

interface ContainerValidContentsQueryResponse {
  type: QueryResultType;
  experimentalRolesByContainerId: ExperimentalRolesByContainerId;
  refetch: () => void;
}

// Undefined is returned when the query is skipped - meaning none of the items are containers. This is done
// intentionally to prevent extra rerenders on the ExpandedItemsTable if there are no meaningful state changes
// See https://github.com/benchling/aurelia/pull/2728 for more details
export function useContainerExperimentalRoleQuery(
  items: Array<ListableItem>
): ContainerValidContentsQueryResponse | undefined {
  const containerIds = items.flatMap((item) => (APIUtil.isContainer(item.id) ? [item.id] : []));
  const result = useSkippableBenchlingQuery<
    ContainersSearchListingExperimentalRoleQuery,
    ContainersSearchListingExperimentalRoleQueryVariables
  >(CONTAINERS_SEARCH_LISTING_EXPERIMENTAL_ROLE_QUERY, {
    skip: containerIds.length === 0,
    variables: {containerIds},
  });

  const baseResponse = {type: result.type, refetch: result.refetch};
  if (result.type === QueryResultType.SKIPPED) {
    return undefined;
  } else if (result.type === QueryResultType.PENDING || result.type === QueryResultType.ERROR) {
    return {...baseResponse, experimentalRolesByContainerId: {}};
  } else {
    return {
      ...baseResponse,
      experimentalRolesByContainerId: objectFromEntries(
        accessibleObjs(result.data.containers).map((item) => [item.id, item.role])
      ),
    };
  }
}
